import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/de';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Suspense } from 'react';
import { Navigate, Outlet, createBrowserRouter, useParams } from 'react-router';

import { useLanguage } from '@/modules/common/application/context/language';
import { LanguageProvider } from '@/modules/common/application/Provider/Language';
import { Loader } from '@/modules/common/components';
import { AssistantWrapper } from '@/modules/common/components/AssistantWrapper';
import PostHogProviderWrapper from '@/modules/common/components/PosthogProviderWrapper/PosthogProviderWrapper';
import { LanguageCode, UserRole } from '@/modules/common/types';
import { FeatureFlag } from '@/modules/common/types';
import MaterialityAssessmentSurveyLayout from '@/pages/survey/MaterialityAssessmentSurveyLayout';

import AppLayout from './app/AppLayout';
import NewAppLayout from './app/NewAppLayout';
import { ErrorBoundary } from './ErrorBoundary';
import GuestRoute from './GuestRoute';
import { NotFound } from './NotFound';
import { NewOnboardingLayout } from './onboarding/NewOnboardingLayout';
import { ProtecedRoute } from './ProtecedRoute';
import PublicLayout from './public/PublicLayout';

const AnalyticsFeatureRoute = ({
  children,
  reportScoped = false,
}: {
  children: React.ReactNode;
  reportScoped?: boolean;
}) => {
  const isAnalyticsEnabled = useFeatureFlagEnabled(FeatureFlag.ANALYTICS);
  const { projectId, reportId } = useParams();

  if (isAnalyticsEnabled !== true) {
    if (reportScoped && reportId) {
      return (
        <Navigate
          to={projectId ? `/project/${projectId}/reporting/${reportId}` : `/reporting/${reportId}`}
          replace
        />
      );
    }

    return <Navigate to="/reporting" replace />;
  }

  return <>{children}</>;
};

const AppDateLocalizationProvider = ({ children }: { children: React.ReactNode }) => {
  const { language } = useLanguage();

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={language === LanguageCode.DE ? 'de' : 'en'}
    >
      {children}
    </LocalizationProvider>
  );
};

export const router = createBrowserRouter([
  {
    element: (
      <LanguageProvider>
        <PostHogProviderWrapper>
          <NuqsAdapter>
            <AssistantWrapper>
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            </AssistantWrapper>
          </NuqsAdapter>
        </PostHogProviderWrapper>
      </LanguageProvider>
    ),
    HydrateFallback: () => <Loader />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/verify-email',
        lazy: async () => {
          const { default: TokenizedLogin } = await import('@/pages/auth/TokenizedLogin');
          return { element: <TokenizedLogin /> };
        },
      },
      {
        Component: GuestRoute,
        children: [
          {
            path: '/login',
            lazy: async () => {
              const { default: Login } = await import('@/pages/auth/Login');
              return { element: <Login /> };
            },
          },
          {
            path: '/passwordless-login',
            lazy: async () => {
              const { default: PasswordlessLogin } = await import('@/pages/auth/PasswordlessLogin');
              return { element: <PasswordlessLogin /> };
            },
          },
          {
            path: '/register',
            lazy: async () => {
              const { default: Register } = await import('@/pages/auth/Register');
              return { element: <Register /> };
            },
          },
          {
            path: '/vsme-waitlist',
            lazy: async () => {
              const { default: Register } = await import('@/pages/auth/Register');
              return { element: <Register /> };
            },
          },
          {
            path: '/resend-verification',
            lazy: async () => {
              const { default: VerifyRegistration } =
                await import('@/pages/auth/VerifyRegistration');
              return { element: <VerifyRegistration /> };
            },
          },
          {
            path: '/resend-email-link',
            lazy: async () => {
              const { default: ResendEmailLink } = await import('@/pages/auth/ResendEmailLink');
              return { element: <ResendEmailLink /> };
            },
          },
          {
            path: '/password-forgot',
            lazy: async () => {
              const { default: PasswordForgot } = await import('@/pages/auth/PasswordForgot');
              return { element: <PasswordForgot /> };
            },
          },
        ],
      },
      {
        Component: ProtecedRoute,
        children: [
          {
            path: '/super',
            handle: {
              allowedRoles: [UserRole.SUPER],
            },
            lazy: async () => {
              const { default: SuperAdmin } = await import('@/pages/SuperAdmin');
              return { element: <SuperAdmin /> };
            },
          },
          {
            path: '/super/reminder',
            handle: {
              allowedRoles: [UserRole.SUPER],
            },
            lazy: async () => {
              const { default: SuperAdmin } = await import('@/pages/SuperAdmin');
              return { element: <SuperAdmin /> };
            },
          },
          {
            path: '/super/storage',
            handle: {
              allowedRoles: [UserRole.SUPER],
            },
            lazy: async () => {
              const { default: SuperAdmin } = await import('@/pages/SuperAdmin');
              return { element: <SuperAdmin /> };
            },
          },
          {
            path: '/company-deactivated',
            handle: {
              allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
            },
            lazy: async () => {
              const { default: CompanyDeactivated } = await import('@/pages/CompanyDeactivated');
              return { element: <CompanyDeactivated /> };
            },
          },
          {
            path: '/cli/authorize',
            handle: {
              allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
            },
            lazy: async () => {
              const { default: CliAuthorize } = await import('@/pages/CliAuthorize');
              return { element: <CliAuthorize /> };
            },
          },
          {
            path: '/oauth/authorize',
            handle: {
              allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
            },
            lazy: async () => {
              const { default: OAuthAuthorize } = await import('@/pages/OAuthAuthorize');
              return { element: <OAuthAuthorize /> };
            },
          },
          {
            Component: MaterialityAssessmentSurveyLayout,
            handle: {
              allowedRoles: [
                UserRole.ADMIN,
                UserRole.EXTERNAL,
                UserRole.EMPLOYEE,
                UserRole.STAKEHOLDER,
              ],
              surveyShell: true,
            },
            children: [
              {
                path: '/materiality-assessment/pre-selection-request',
                lazy: async () => {
                  const { default: MaterialityPreSelectionSurvey } =
                    await import('@/pages/survey/MaterialityPreSelectionSurvey');
                  return { element: <MaterialityPreSelectionSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/pre-selection-request/:preSelectionRequestId',
                lazy: async () => {
                  const { default: MaterialityPreSelectionSurvey } =
                    await import('@/pages/survey/MaterialityPreSelectionSurvey');
                  return { element: <MaterialityPreSelectionSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/pre-selection-request/:preSelectionRequestId/instructions',
                lazy: async () => {
                  const { default: MaterialityPreSelectionSurvey } =
                    await import('@/pages/survey/MaterialityPreSelectionSurvey');
                  return { element: <MaterialityPreSelectionSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/pre-selection-request/:preSelectionRequestId/topics/:materialityAssessmentTopicId',
                lazy: async () => {
                  const { default: MaterialityPreSelectionSurvey } =
                    await import('@/pages/survey/MaterialityPreSelectionSurvey');
                  return { element: <MaterialityPreSelectionSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/pre-selection-request/:preSelectionRequestId/additional-topics',
                lazy: async () => {
                  const { default: MaterialityPreSelectionSurvey } =
                    await import('@/pages/survey/MaterialityPreSelectionSurvey');
                  return { element: <MaterialityPreSelectionSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/pre-selection-request/:preSelectionRequestId/summary',
                lazy: async () => {
                  const { default: MaterialityPreSelectionSurvey } =
                    await import('@/pages/survey/MaterialityPreSelectionSurvey');
                  return { element: <MaterialityPreSelectionSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/instructions',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/summary',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/topic/:materialityAssessmentTopicId/:topicType',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/topic/:materialityAssessmentTopicId/:topicType/info',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/topic/:materialityAssessmentTopicId/:topicType/define',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/topic/:materialityAssessmentTopicId/:topicType/evaluate',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/topics/:rootTopicId/overview',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/topics/:rootTopicId/matters/:matterTopicId',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/topics/:rootTopicId/evaluation',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-request/:evaluationRequestId/topics/:rootTopicId/evaluation/riskOpportunity',
                lazy: async () => {
                  const { default: MaterialityEvaluationRequestSurvey } =
                    await import('@/pages/survey/MaterialityEvaluationRequestSurvey');
                  return { element: <MaterialityEvaluationRequestSurvey /> };
                },
              },
            ],
          },
          {
            element: (
              <AppDateLocalizationProvider>
                <NewAppLayout />
              </AppDateLocalizationProvider>
            ),
            children: [
              {
                path: '/home',
                lazy: async () => {
                  const { default: ChatOverview } = await import('@/pages/app/ChatOverview');
                  return { element: <ChatOverview /> };
                },
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'home',
                      to: '/home',
                      active: true,
                    },
                  ],
                },
              },
              {
                path: '/chat',
                lazy: async () => {
                  const { default: ChatOverview } = await import('@/pages/app/ChatOverview');
                  return { element: <ChatOverview /> };
                },
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'chat',
                      to: '/chat',
                      active: true,
                    },
                  ],
                },
              },
              {
                path: '/chat/:conversationId',
                lazy: async () => {
                  const { default: ChatConversation } =
                    await import('@/pages/app/ChatConversation');
                  return { element: <ChatConversation /> };
                },
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'chat',
                      to: '/chat',
                    },
                  ],
                },
              },
              {
                path: '/tasks',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'tasks',
                      to: '/tasks',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Tasks } = await import('@/pages/app/Tasks');
                  return { element: <Tasks /> };
                },
              },
              {
                path: '/reporting',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'reporting',
                      to: '/reporting',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Reports } = await import('@/pages/app/Reports');
                  return { element: <Reports /> };
                },
              },
              {
                path: '/reporting/:reportId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'reporting',
                      to: '/reporting',
                    },
                    {
                      to: '/reporting/:reportId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'newReport',
                        paramKey: 'reportId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: ReportDetails } = await import('@/pages/app/ReportDetails');
                  return { element: <ReportDetails /> };
                },
              },
              {
                path: '/reporting/:reportId/analytics',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'analytics',
                      to: '/reporting/:reportId/analytics',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: ReportAnalytics } = await import('@/pages/app/ReportAnalytics');
                  return {
                    element: (
                      <AnalyticsFeatureRoute reportScoped>
                        <ReportAnalytics />
                      </AnalyticsFeatureRoute>
                    ),
                  };
                },
              },
              {
                path: '/reporting/:reportId/:reportChapterId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'reporting',
                      to: '/reporting',
                    },
                    {
                      to: '/reporting/:reportId/:reportChapterId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'newReport',
                        paramKey: 'reportId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: ReportDetails } = await import('@/pages/app/ReportDetails');
                  return { element: <ReportDetails /> };
                },
              },
              {
                path: '/analytics',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'analytics',
                      to: '/analytics',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Analytics } = await import('@/pages/app/Analytics');
                  return {
                    element: (
                      <AnalyticsFeatureRoute>
                        <Analytics />
                      </AnalyticsFeatureRoute>
                    ),
                  };
                },
              },
              {
                path: '/analytics/:dashboardId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'analytics',
                      to: '/analytics',
                    },
                    {
                      to: '/analytics/:dashboardId',
                      active: true,
                      label: 'dashboard',
                      dynamicBreadcrumb: {
                        type: 'analyticsDashboard',
                        paramKey: 'dashboardId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: AnalyticsDashboard } =
                    await import('@/pages/app/AnalyticsDashboard');
                  return {
                    element: (
                      <AnalyticsFeatureRoute>
                        <AnalyticsDashboard />
                      </AnalyticsFeatureRoute>
                    ),
                  };
                },
              },
              {
                path: '/custom-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'datapoints',
                      to: '/custom-data',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: CustomData } = await import('@/pages/app/CustomData');
                  return { element: <CustomData /> };
                },
              },
              {
                path: '/custom-data/:dataPointId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'datapoints',
                      to: '/custom-data',
                    },
                    {
                      to: '/custom-data/:dataPointId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'dataPoint',
                        paramKey: 'dataPointId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: CustomDataDetail } =
                    await import('@/pages/app/CustomDataDetail');
                  return { element: <CustomDataDetail /> };
                },
              },
              {
                path: '/products',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'products',
                      to: '/products',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Products } = await import('@/pages/app/Products');
                  return { element: <Products /> };
                },
              },
              {
                path: '/products/:productId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'products',
                      to: '/products',
                    },
                    {
                      to: '/products/:productId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'product',
                        paramKey: 'productId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: ProductDetails } = await import('@/pages/app/ProductDetails');
                  return { element: <ProductDetails /> };
                },
              },
              {
                path: '/suppliers',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'suppliers',
                      to: '/suppliers',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Suppliers } = await import('@/pages/app/Suppliers');
                  return { element: <Suppliers /> };
                },
              },
              {
                path: '/suppliers/:supplierId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'suppliers',
                      to: '/suppliers',
                    },
                    {
                      to: '/suppliers/:supplierId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'supplier',
                        paramKey: 'supplierId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: SupplierDetails } = await import('@/pages/app/SupplierDetails');
                  return { element: <SupplierDetails /> };
                },
              },
              {
                path: '/supplier-connection-requests/:requestId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'suppliers',
                      to: '/suppliers',
                    },
                    {
                      label: 'supplierConnectionRequest',
                      to: '/supplier-connection-requests/:requestId',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: SupplierConnectionRequest } =
                    await import('@/pages/app/SupplierConnectionRequest');
                  return { element: <SupplierConnectionRequest /> };
                },
              },
              {
                path: '/emission-factors',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'emissionFactors',
                      to: '/emission-factors',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: EmissionFactors } = await import('@/pages/app/EmissionFactors');
                  return { element: <EmissionFactors /> };
                },
              },
              {
                path: '/project/:projectId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'project',
                      to: '/project/:projectId',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Reports } = await import('@/pages/app/Reports');
                  return { element: <Reports /> };
                },
              },
              {
                path: '/project/:projectId/reporting',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'reporting',
                      to: '/project/:projectId/reporting',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Reports } = await import('@/pages/app/Reports');
                  return { element: <Reports /> };
                },
              },
              {
                path: '/project/:projectId/reporting/:reportId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'reporting',
                      to: '/project/:projectId/reporting',
                    },
                    {
                      to: '/project/:projectId/reporting/:reportId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'newReport',
                        paramKey: 'reportId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: ReportDetails } = await import('@/pages/app/ReportDetails');
                  return { element: <ReportDetails /> };
                },
              },
              {
                path: '/project/:projectId/reporting/:reportId/analytics',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'analytics',
                      to: '/project/:projectId/reporting/:reportId/analytics',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: ReportAnalytics } = await import('@/pages/app/ReportAnalytics');
                  return {
                    element: (
                      <AnalyticsFeatureRoute reportScoped>
                        <ReportAnalytics />
                      </AnalyticsFeatureRoute>
                    ),
                  };
                },
              },
              {
                path: '/project/:projectId/reporting/:reportId/:reportChapterId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'reporting',
                      to: '/project/:projectId/reporting',
                    },
                    {
                      to: '/project/:projectId/reporting/:reportId/:reportChapterId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'newReport',
                        paramKey: 'reportId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: ReportDetails } = await import('@/pages/app/ReportDetails');
                  return { element: <ReportDetails /> };
                },
              },
              {
                path: '/project/:projectId/analytics',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'analytics',
                      to: '/project/:projectId/analytics',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Analytics } = await import('@/pages/app/Analytics');
                  return {
                    element: (
                      <AnalyticsFeatureRoute>
                        <Analytics />
                      </AnalyticsFeatureRoute>
                    ),
                  };
                },
              },
              {
                path: '/project/:projectId/analytics/:dashboardId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'analytics',
                      to: '/project/:projectId/analytics',
                    },
                    {
                      to: '/project/:projectId/analytics/:dashboardId',
                      active: true,
                      label: 'dashboard',
                      dynamicBreadcrumb: {
                        type: 'analyticsDashboard',
                        paramKey: 'dashboardId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: AnalyticsDashboard } =
                    await import('@/pages/app/AnalyticsDashboard');
                  return {
                    element: (
                      <AnalyticsFeatureRoute>
                        <AnalyticsDashboard />
                      </AnalyticsFeatureRoute>
                    ),
                  };
                },
              },
              {
                path: '/project/:projectId/custom-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'datapoints',
                      to: '/project/:projectId/custom-data',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: CustomData } = await import('@/pages/app/CustomData');
                  return { element: <CustomData /> };
                },
              },
              {
                path: '/project/:projectId/custom-data/:dataPointId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'datapoints',
                      to: '/project/:projectId/custom-data',
                    },
                    {
                      to: '/project/:projectId/custom-data/:dataPointId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'dataPoint',
                        paramKey: 'dataPointId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: CustomDataDetail } =
                    await import('@/pages/app/CustomDataDetail');
                  return { element: <CustomDataDetail /> };
                },
              },
              {
                path: '/project/:projectId/products',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'products',
                      to: '/project/:projectId/products',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Products } = await import('@/pages/app/Products');
                  return { element: <Products /> };
                },
              },
              {
                path: '/project/:projectId/products/:productId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'products',
                      to: '/project/:projectId/products',
                    },
                    {
                      to: '/project/:projectId/products/:productId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'product',
                        paramKey: 'productId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: ProductDetails } = await import('@/pages/app/ProductDetails');
                  return { element: <ProductDetails /> };
                },
              },
              {
                path: '/project/:projectId/suppliers',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'suppliers',
                      to: '/project/:projectId/suppliers',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Suppliers } = await import('@/pages/app/Suppliers');
                  return { element: <Suppliers /> };
                },
              },
              {
                path: '/project/:projectId/suppliers/:supplierId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'suppliers',
                      to: '/project/:projectId/suppliers',
                    },
                    {
                      to: '/project/:projectId/suppliers/:supplierId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'supplier',
                        paramKey: 'supplierId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: SupplierDetails } = await import('@/pages/app/SupplierDetails');
                  return { element: <SupplierDetails /> };
                },
              },
              {
                path: '/project/:projectId/emission-factors',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                  breadcrumb: [
                    {
                      label: 'emissionFactors',
                      to: '/project/:projectId/emission-factors',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: EmissionFactors } = await import('@/pages/app/EmissionFactors');
                  return { element: <EmissionFactors /> };
                },
              },
              {
                path: '/project/:projectId/documents',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'documents',
                      to: '/project/:projectId/documents',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Documents } = await import('@/pages/app/Documents');
                  return { element: <Documents /> };
                },
              },
              {
                path: '/project/:projectId/documents/folder/:folderId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'documents',
                      to: '/project/:projectId/documents',
                    },
                    {
                      label: 'folder',
                      to: '/project/:projectId/documents/folder/:folderId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'documentFolder',
                        paramKey: 'folderId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Documents } = await import('@/pages/app/Documents');
                  return { element: <Documents /> };
                },
              },
              {
                path: '/project/:projectId/documents/:documentId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'documents',
                      to: '/project/:projectId/documents',
                    },
                    {
                      to: '/project/:projectId/documents/:documentId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'document',
                        paramKey: 'documentId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: DocumentDetail } = await import('@/pages/app/DocumentDetails');
                  return { element: <DocumentDetail /> };
                },
              },
              {
                path: '/documents',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'documents',
                      to: '/documents',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Documents } = await import('@/pages/app/Documents');
                  return { element: <Documents /> };
                },
              },
              {
                path: '/documents/folder/:folderId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'documents',
                      to: '/documents',
                    },
                    {
                      label: 'folder',
                      to: '/documents/folder/:folderId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'documentFolder',
                        paramKey: 'folderId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: Documents } = await import('@/pages/app/Documents');
                  return { element: <Documents /> };
                },
              },
              {
                path: '/documents/:documentId',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'documents',
                      to: '/documents',
                    },
                    {
                      to: '/documents/:documentId',
                      active: true,
                      dynamicBreadcrumb: {
                        type: 'document',
                        paramKey: 'documentId',
                      },
                    },
                  ],
                },
                lazy: async () => {
                  const { default: DocumentDetail } = await import('@/pages/app/DocumentDetails');
                  return { element: <DocumentDetail /> };
                },
              },
              {
                path: '/questionnaire',
                lazy: async () => {
                  const { default: QuestionnairePage } =
                    await import('@/pages/app/QuestionnairePage');
                  return { element: <QuestionnairePage /> };
                },
              },
              {
                path: '/app/settings',
                handle: {
                  allowedRoles: [
                    UserRole.ADMIN,
                    UserRole.EMPLOYEE,
                    UserRole.VIEWER,
                    UserRole.EXTERNAL,
                  ],
                  breadcrumb: [
                    {
                      label: 'settings',
                      to: '/app/settings',
                      active: true,
                    },
                  ],
                },
                lazy: async () => {
                  const { default: NewSettings } = await import('@/pages/app/NewSettings');
                  return { element: <NewSettings /> };
                },
                children: [
                  {
                    index: true,
                    element: <Navigate to="/app/settings/general" replace />,
                  },
                  {
                    path: 'general',
                    lazy: async () => {
                      const { default: GeneralSettings } =
                        await import('@/pages/app/settings/GeneralSettings');
                      return { element: <GeneralSettings /> };
                    },
                  },
                  {
                    path: 'user',
                    lazy: async () => {
                      const { default: UserProfileSettings } =
                        await import('@/pages/app/settings/UserProfileSettings');
                      return { element: <UserProfileSettings /> };
                    },
                  },
                  {
                    path: 'company',
                    handle: {
                      // External users cannot access company settings
                      allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                    },
                    lazy: async () => {
                      const { default: CompanySettings } =
                        await import('@/pages/app/settings/CompanySettings');
                      return { element: <CompanySettings /> };
                    },
                  },
                  {
                    path: 'usage',
                    handle: {
                      // External users cannot access company usage settings
                      allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                    },
                    lazy: async () => {
                      const { default: UsageSettings } =
                        await import('@/pages/app/settings/UsageSettings');
                      return { element: <UsageSettings /> };
                    },
                  },
                  {
                    path: 'ai-context',
                    handle: {
                      allowedRoles: [UserRole.ADMIN],
                    },
                    lazy: async () => {
                      const { default: AIContextSettings } =
                        await import('@/pages/app/settings/AIContextSettings');
                      return { element: <AIContextSettings /> };
                    },
                  },
                  {
                    path: 'members',
                    handle: {
                      // External users cannot access members settings
                      allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                    },
                    lazy: async () => {
                      const { default: MemberSettings } =
                        await import('@/pages/app/settings/MemberSettings');
                      return { element: <MemberSettings /> };
                    },
                  },
                  {
                    path: 'cli-keys',
                    handle: {
                      // External users cannot access CLI key settings
                      allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                    },
                    lazy: async () => {
                      const { default: CliKeySettings } =
                        await import('@/pages/app/settings/CliKeySettings');
                      return { element: <CliKeySettings /> };
                    },
                  },
                  {
                    path: 'facilities',
                    handle: {
                      // External users cannot access facilities settings
                      allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                    },
                    lazy: async () => {
                      const { default: FacilitySettings } =
                        await import('@/pages/app/settings/FacilitySettings');
                      return { element: <FacilitySettings /> };
                    },
                  },
                ],
              },
            ],
          },
          {
            element: (
              <AppDateLocalizationProvider>
                <AppLayout />
              </AppDateLocalizationProvider>
            ),
            children: [
              {
                index: true,
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Home } = await import('@/pages/app/Home');
                  return { element: <Home /> };
                },
              },
              {
                path: 'recent-activity',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: RecentActivity } = await import('@/pages/app/RecentActivity');
                  return { element: <RecentActivity /> };
                },
              },
              {
                path: '/materiality-assessment',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityAssessment } =
                    await import('@/pages/app/MaterialityAssessment');
                  return { element: <MaterialityAssessment /> };
                },
              },
              {
                path: '/materiality-assessment/stakeholder-mapping',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityAssessment } =
                    await import('@/pages/app/MaterialityAssessment');
                  return { element: <MaterialityAssessment /> };
                },
              },
              {
                path: '/materiality-assessment/pre-selection',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityAssessment } =
                    await import('@/pages/app/MaterialityAssessment');
                  return { element: <MaterialityAssessment /> };
                },
              },
              {
                path: '/materiality-assessment/predefine-iros',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityAssessment } =
                    await import('@/pages/app/MaterialityAssessment');
                  return { element: <MaterialityAssessment /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityAssessment } =
                    await import('@/pages/app/MaterialityAssessment');
                  return { element: <MaterialityAssessment /> };
                },
              },
              {
                path: '/materiality-assessment/settings',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityAssessment } =
                    await import('@/pages/app/MaterialityAssessment');
                  return { element: <MaterialityAssessment /> };
                },
              },
              {
                path: '/materiality-assessment/legacy-evaluation',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityAssessment } =
                    await import('@/pages/app/MaterialityAssessment');
                  return { element: <MaterialityAssessment /> };
                },
              },
              {
                path: '/materiality-assessment/iro-migration',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityAssessment } =
                    await import('@/pages/app/MaterialityAssessment');
                  return { element: <MaterialityAssessment /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation-legacy/:materialityAssessmentTopicId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityEvaluationLegacy } =
                    await import('@/pages/app/MaterialityEvaluationLegacy');
                  return { element: <MaterialityEvaluationLegacy /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation/:materialityAssessmentTopicId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityEvaluation } =
                    await import('@/pages/app/MaterialityEvaluation');
                  return { element: <MaterialityEvaluation /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation/:materialityAssessmentTopicId/impact',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityEvaluation } =
                    await import('@/pages/app/MaterialityEvaluation');
                  return { element: <MaterialityEvaluation /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation/:materialityAssessmentTopicId/financial',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: MaterialityEvaluation } =
                    await import('@/pages/app/MaterialityEvaluation');
                  return { element: <MaterialityEvaluation /> };
                },
              },
              {
                path: '/materiality-assessment/evaluation/:materialityAssessmentTopicId/sustainability-matter',
                handle: {
                  allowedRoles: [UserRole.ADMIN],
                },
                lazy: async () => {
                  const { default: MaterialityEvaluation } =
                    await import('@/pages/app/MaterialityEvaluation');
                  return { element: <MaterialityEvaluation /> };
                },
              },
              {
                path: '/data-management',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: DataManagement } = await import('@/pages/app/DataManagement');
                  return { element: <DataManagement /> };
                },
              },
              {
                path: '/data-management/collect',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: DataManagement } = await import('@/pages/app/DataManagement');
                  return { element: <DataManagement /> };
                },
              },
              {
                path: '/data-management/collect-global',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: DataManagement } = await import('@/pages/app/DataManagement');
                  return { element: <DataManagement /> };
                },
              },
              {
                path: '/data-management/collect/:collectOverviewId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: EsgDataInput } = await import('@/pages/app/EsgDataInput');
                  return { element: <EsgDataInput /> };
                },
              },
              {
                path: '/data-management/collect/:collectOverviewId/numeric-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: EsgDataInput } = await import('@/pages/app/EsgDataInput');
                  return { element: <EsgDataInput /> };
                },
              },
              {
                path: '/data-management/collect/:collectOverviewId/approval',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: EsgDataInput } = await import('@/pages/app/EsgDataInput');
                  return { element: <EsgDataInput /> };
                },
              },
              {
                path: '/data-management/collect/:collectOverviewId/history',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: EsgDataInput } = await import('@/pages/app/EsgDataInput');
                  return { element: <EsgDataInput /> };
                },
              },
              {
                path: '/data-management/collect/:collectOverviewId/descriptive-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: EsgDataInput } = await import('@/pages/app/EsgDataInput');
                  return { element: <EsgDataInput /> };
                },
              },
              {
                path: '/data-management/collect/:collectOverviewId/entry-questions',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: EsgDataInput } = await import('@/pages/app/EsgDataInput');
                  return { element: <EsgDataInput /> };
                },
              },
              {
                path: '/data-management/select-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: DataManagement } = await import('@/pages/app/DataManagement');
                  return { element: <DataManagement /> };
                },
              },
              {
                path: '/data-management/select-data/:reportChapterId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: SelectDataDetailContainer } =
                    await import('@/pages/app/SelectDataDetail');
                  return { element: <SelectDataDetailContainer /> };
                },
              },
              {
                path: '/data-management/select-data/:reportChapterId/select-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: SelectDataDetailContainer } =
                    await import('@/pages/app/SelectDataDetail');
                  return { element: <SelectDataDetailContainer /> };
                },
              },
              {
                path: '/analysis',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Analysis } = await import('@/pages/app/Analysis');
                  return { element: <Analysis /> };
                },
              },
              {
                path: '/analysis/social',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Analysis } = await import('@/pages/app/Analysis');
                  return { element: <Analysis /> };
                },
              },
              {
                path: '/analysis/governance',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Analysis } = await import('@/pages/app/Analysis');
                  return { element: <Analysis /> };
                },
              },
              {
                path: '/goal-action',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: GoalAndAction } = await import('@/pages/app/GoalAndAction');
                  return { element: <GoalAndAction /> };
                },
              },
              {
                path: '/goal-action/:chapterId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: GoalAndActionDetail } =
                    await import('@/pages/app/GoalAndActionDetail');
                  return { element: <GoalAndActionDetail /> };
                },
              },
              {
                path: '/reports',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Reporting } = await import('@/pages/app/Reporting');
                  return { element: <Reporting /> };
                },
              },
              {
                path: '/reports/:reportId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId/relevant-quantitative-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId/relevant-narrative-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId/relevant-semi-narrative-data',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId/examples',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId/history',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId/comment',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId/goals',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/reports/:reportId/section/:sectionId/actions',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: ReportSectionPage } =
                    await import('@/pages/app/ReportSectionPage');
                  return { element: <ReportSectionPage /> };
                },
              },
              {
                path: '/assistant',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Assistant } = await import('@/pages/app/Assistant');
                  return { element: <Assistant /> };
                },
              },
              {
                path: '/settings',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Settings } = await import('@/pages/app/Settings');
                  return { element: <Settings /> };
                },
              },
              {
                path: '/settings/user-info',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Settings } = await import('@/pages/app/Settings');
                  return { element: <Settings /> };
                },
              },
              {
                path: '/settings/business-info',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Settings } = await import('@/pages/app/Settings');
                  return { element: <Settings /> };
                },
              },
              {
                path: '/settings/manage-team',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Settings } = await import('@/pages/app/Settings');
                  return { element: <Settings /> };
                },
              },
              {
                path: '/settings/manage-facility',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Settings } = await import('@/pages/app/Settings');
                  return { element: <Settings /> };
                },
              },
              {
                path: '/settings/security',
                handle: {
                  allowedRoles: [UserRole.ADMIN],
                },
                lazy: async () => {
                  const { default: Settings } = await import('@/pages/app/Settings');
                  return { element: <Settings /> };
                },
              },
              {
                path: '/settings/other-settings',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.VIEWER],
                },
                lazy: async () => {
                  const { default: Settings } = await import('@/pages/app/Settings');
                  return { element: <Settings /> };
                },
              },
              {
                path: '/settings/assistant',
                handle: {
                  allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE],
                },
                lazy: async () => {
                  const { default: Settings } = await import('@/pages/app/Settings');
                  return { element: <Settings /> };
                },
              },
            ],
          },
          {
            element: <PublicLayout />,
            children: [
              {
                path: '/shareable',
                handle: {
                  allowedRoles: [UserRole.EXTERNAL],
                },
                lazy: async () => {
                  const { default: PublicOverview } = await import('@/pages/public/PublicOverview');
                  return { element: <PublicOverview /> };
                },
              },
              {
                path: '/shareable/:userId/:year/facilities/:facilityId/categories/:categoryId',
                handle: {
                  allowedRoles: [UserRole.EXTERNAL],
                },
                lazy: async () => {
                  const { default: PublicOverview } = await import('@/pages/public/PublicOverview');
                  return { element: <PublicOverview /> };
                },
              },
              {
                path: '/shareable/:collectOverviewId',
                handle: {
                  allowedRoles: [UserRole.EXTERNAL],
                },
                lazy: async () => {
                  const { default: PublicCollectInput } =
                    await import('@/pages/public/PublicCollectInput');
                  return { element: <PublicCollectInput /> };
                },
              },
            ],
          },
          {
            Component: NewOnboardingLayout,
            children: [
              {
                path: '/set-password',
                lazy: async () => {
                  const { default: SetPassword } = await import('@/pages/onboarding/SetPassword');
                  return { element: <SetPassword /> };
                },
              },
              {
                path: '/user-info',
                lazy: async () => {
                  const { default: UserInfo } = await import('@/pages/onboarding/UserInfo');
                  return { element: <UserInfo /> };
                },
              },
              {
                path: '/onboarding-start',
                lazy: async () => {
                  const { default: OnboardingStart } =
                    await import('@/pages/onboarding/OnboardingStart');
                  return { element: <OnboardingStart /> };
                },
              },
              {
                path: '/company-info',
                lazy: async () => {
                  const { default: CompanyInfo } = await import('@/pages/onboarding/CompanyInfo');
                  return { element: <CompanyInfo /> };
                },
              },
              {
                path: '/add-facility',
                lazy: async () => {
                  const { default: AddFacility } = await import('@/pages/onboarding/AddFacility');
                  return { element: <AddFacility /> };
                },
              },
            ],
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
    ],
  },
]);
